import {
	getOverlappingPatientsOnChair,
	isChairEligibleForPatient
} from "../../utils/realTimeChairUtils"

import { hasOverlap } from "../../utils/realTimeUtils"
import { PatientNotFoundError } from "./errors"

/**
 * ChairsWithFutureAssignments is a wrapper around some of the
 * realTimeChairUtils functions that allows us to handle modifications

 * that are slated to be made in reassignments
 */
class ChairsWithFutureAssignments {
	//a map of chairId to the list of patientIds that are slated to be added to that chair
	existingAdditions = new Map()
	//a map of chairId to the list of patientIds that are slated to be removed from that chair
	existingRemovals = new Map()

	/**
	 *
	 * @param {{patientSchedule: {patientId: number, infusionType: string}[]}} schedule
	 * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
	 */
	constructor(schedule, acuityTable) {
		this.schedule = schedule
		this.acuityTable = acuityTable
	}

	addPatientToChair(patientId, chairId) {
		const existingRemovals = this.existingRemovals.get(chairId)
		if (existingRemovals) {
			this.existingRemovals.set(
				chairId,
				existingRemovals.filter(id => id !== patientId)
			)
		}

		const existingAdditions = this.existingAdditions.get(chairId)
		if (existingAdditions) {
			if (existingAdditions.includes(patientId)) {
				return
			}
			existingAdditions.push(patientId)
		} else {
			this.existingAdditions.set(chairId, [patientId])
		}
	}

	removePatientFromChair(patientId, chairId) {
		const existingAdditions = this.existingAdditions.get(chairId)
		if (existingAdditions) {
			this.existingAdditions.set(
				chairId,
				existingAdditions.filter(id => id !== patientId)
			)
		}

		const existingRemovals = this.existingRemovals.get(chairId)
		if (existingRemovals) {
			if (existingRemovals.includes(patientId)) {
				return
			}
			existingRemovals.push(patientId)
		} else {
			this.existingRemovals.set(chairId, [patientId])
		}
	}

	/**
	 * Returns a list of overlapping patinet's ids
	 * @param {{assignedPatients: number[]}} chair
	 * @param {{patientId: number, infusionType: string}} newPatient
	 * @param {{patientSchedule: {patientId: number, infusionType: string}[]}} schedule
	 * @returns {number[]} a list of overlapping patient's ids
	 */
	getOverlappingPatientsOnChair(chair, newPatient, sandbox, isTomorrowMode) {
		const rawOverlappingPatients = getOverlappingPatientsOnChair(
			chair,
			newPatient,
			this.schedule,
			sandbox,
			isTomorrowMode
		)

		const removals = this.existingRemovals.get(chair.chairId) || []
		const overlappingPatients = rawOverlappingPatients.filter(id => !removals.includes(id))

		//check if any of the additions are overlapping with the new patient
		const additions = this.existingAdditions.get(chair.chairId) || []
		for (const patientId of additions) {
			const patientObject = this.schedule.patientSchedule.find(p => p.patientId === patientId)
			if (!patientObject) {
				throw new PatientNotFoundError(
					patientId,
					"Today's schedule",
					this.schedule.patientSchedule
				)
			}
			if (hasOverlap(patientObject, newPatient, sandbox, isTomorrowMode)) {
				overlappingPatients.push(patientId)
			}
		}

		return overlappingPatients
	}

	/**
   * Finds the list of chair ids that are available for a new patient

  *
  * Checks for overlapping patients and for chair eligability rules
  * @param {{patientId: number, infusionType: string}} newPatient
  * @param {{infusionType: {chairEligability: {chairId: {eligible: boolean}}}}[]} acuityTable
  * 
  * @returns {number[]} an array of chair ids that are available for the new patient
  */
	getChairAvailabilityForPatient = (newPatient, sandbox, isTomorrowMode) => {
		const chairIds = []
		for (const chair of this.schedule.chairSchedule) {
			if (!isChairEligibleForPatient(chair, newPatient, this.acuityTable)) {
				continue
			}

			const overlappingPatients = this.getOverlappingPatientsOnChair(chair, newPatient, sandbox, isTomorrowMode)
			if (overlappingPatients.length > 0) {
				continue
			}

			chairIds.push(chair.chairId)
		}
		return chairIds
	}
}

export default ChairsWithFutureAssignments
